import React, { useState, useEffect } from 'react';
import { Plus, Globe, ExternalLink, Edit, Trash2, TrendingUp, Users, Clock } from 'lucide-react';

const Websites = () => {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    url: '',
    category: 'kurumsal',
    technology: ['React'],
    status: 'geliştirme',
    client: {
      name: '',
      email: '',
      company: ''
    },
    team: {
      developer: '',
      designer: '',
      projectManager: ''
    },
    metrics: {
      monthlyVisitors: 0,
      pageViews: 0,
      seoScore: 0
    },
    pricing: {
      projectCost: 0,
      monthlyMaintenance: 0
    },
    features: {
      responsive: true,
      seo: true,
      cms: false,
      ecommerce: false
    }
  });

  useEffect(() => {
    fetchWebsites();
  }, []);

  const fetchWebsites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/websites', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setWebsites(data.websites);
      }
    } catch (error) {
      console.error('Web siteleri yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const url = editingWebsite ? `/api/websites/${editingWebsite._id}` : '/api/websites';
      const method = editingWebsite ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        fetchWebsites();
        setShowModal(false);
        resetForm();
      }
    } catch (error) {
      console.error('Web sitesi kaydetme hatası:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu web sitesini silmek istediğinizden emin misiniz?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/websites/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        if (data.success) {
          fetchWebsites();
        }
      } catch (error) {
        console.error('Web sitesi silme hatası:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      url: '',
      category: 'kurumsal',
      technology: ['React'],
      status: 'geliştirme',
      client: { name: '', email: '', company: '' },
      team: { developer: '', designer: '', projectManager: '' },
      metrics: { monthlyVisitors: 0, pageViews: 0, seoScore: 0 },
      pricing: { projectCost: 0, monthlyMaintenance: 0 },
      features: { responsive: true, seo: true, cms: false, ecommerce: false }
    });
    setEditingWebsite(null);
  };

  const openEditModal = (website) => {
    setEditingWebsite(website);
    setFormData({
      name: website.name,
      description: website.description || '',
      url: website.url,
      category: website.category,
      technology: website.technology || ['React'],
      status: website.status,
      client: website.client || { name: '', email: '', company: '' },
      team: {
        developer: website.team?.developer?.username || '',
        designer: website.team?.designer?.username || '',
        projectManager: website.team?.projectManager?.username || ''
      },
      metrics: website.metrics || { monthlyVisitors: 0, pageViews: 0, seoScore: 0 },
      pricing: website.pricing || { projectCost: 0, monthlyMaintenance: 0 },
      features: website.features || { responsive: true, seo: true, cms: false, ecommerce: false }
    });
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'geliştirme': 'bg-yellow-100 text-yellow-800',
      'test': 'bg-blue-100 text-blue-800',
      'canlı': 'bg-green-100 text-green-800',
      'bakım': 'bg-orange-100 text-orange-800',
      'durduruldu': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'kurumsal': '🏢',
      'e-ticaret': '🛒',
      'blog': '📝',
      'portfolio': '🎨',
      'landing': '🚀',
      'diğer': '🌐'
    };
    return icons[category] || '🌐';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Web Siteleri</h1>
          <p className="text-gray-600">Geliştirilen web sitelerini yönetin</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Yeni Web Sitesi
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Globe className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Toplam Site</p>
              <p className="text-2xl font-bold text-gray-900">{websites.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <TrendingUp className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Canlı Siteler</p>
              <p className="text-2xl font-bold text-gray-900">
                {websites.filter(w => w.status === 'canlı').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Geliştirmede</p>
              <p className="text-2xl font-bold text-gray-900">
                {websites.filter(w => w.status === 'geliştirme').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Toplam Ziyaret</p>
              <p className="text-2xl font-bold text-gray-900">
                {websites.reduce((sum, w) => sum + (w.metrics?.monthlyVisitors || 0), 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Websites Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {websites.map((website) => (
          <div key={website._id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">{getCategoryIcon(website.category)}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{website.name}</h3>
                    <p className="text-sm text-gray-500">{website.category}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(website.status)}`}>
                  {website.status}
                </span>
              </div>

              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {website.description || 'Açıklama eklenmemiş'}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-500">
                  <Globe className="h-4 w-4 mr-2" />
                  <a href={website.url} target="_blank" rel="noopener noreferrer" 
                     className="text-blue-600 hover:underline truncate">
                    {website.url}
                  </a>
                  <ExternalLink className="h-3 w-3 ml-1" />
                </div>
                
                {website.team?.developer && (
                  <div className="flex items-center text-sm text-gray-500">
                    <Users className="h-4 w-4 mr-2" />
                    <span>Geliştirici: {website.team.developer.username}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <span className="text-gray-500">Aylık Ziyaret:</span>
                  <p className="font-semibold">{(website.metrics?.monthlyVisitors || 0).toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-500">SEO Skoru:</span>
                  <p className="font-semibold">{website.metrics?.seoScore || 0}/100</p>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="flex space-x-2">
                  {website.technology?.slice(0, 2).map((tech, index) => (
                    <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                      {tech}
                    </span>
                  ))}
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => openEditModal(website)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(website._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {websites.length === 0 && (
        <div className="text-center py-12">
          <Globe className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Henüz web sitesi yok</h3>
          <p className="mt-1 text-sm text-gray-500">İlk web sitenizi ekleyerek başlayın.</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingWebsite ? 'Web Sitesi Düzenle' : 'Yeni Web Sitesi Ekle'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Site Adı</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">URL</label>
                    <input
                      type="url"
                      required
                      value={formData.url}
                      onChange={(e) => setFormData({...formData, url: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Açıklama</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Kategori</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="kurumsal">Kurumsal</option>
                      <option value="e-ticaret">E-ticaret</option>
                      <option value="blog">Blog</option>
                      <option value="portfolio">Portfolio</option>
                      <option value="landing">Landing</option>
                      <option value="diğer">Diğer</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Durum</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    >
                      <option value="geliştirme">Geliştirme</option>
                      <option value="test">Test</option>
                      <option value="canlı">Canlı</option>
                      <option value="bakım">Bakım</option>
                      <option value="durduruldu">Durduruldu</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Proje Maliyeti (₺)</label>
                    <input
                      type="number"
                      value={formData.pricing.projectCost}
                      onChange={(e) => setFormData({
                        ...formData, 
                        pricing: {...formData.pricing, projectCost: Number(e.target.value)}
                      })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    {editingWebsite ? 'Güncelle' : 'Ekle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Websites;